import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { composeValidators, maxLengthValidation, noHtmlPatternValidation, requiredValidation } from 'validators';
import { Checkbox, Spinner, Callout, ConfirmationModal, TextField, Typography } from 'seagull';
import { getCustomMessageValidation } from '~/business-logic/validators';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { NotificationEditField } from '~/components/NotificationEditField/NotificationEditField';
import { useStore, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { helpArticles } from '~/utils/helpArticles';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { useRestfulWrapper } from '../useRestfulWrapper';
import styles from './AgreementTerminationModal.module.scss';
const standardVariantKey = 'standard';
const emailContentLabel = 'Email content';
const emailSubjectLabel = 'Email subject';
const emailContentRules = getCustomMessageValidation(emailContentLabel, true);
const emailSubjectRules = composeValidators([
    requiredValidation('Required'),
    maxLengthValidation(120, emailSubjectLabel),
    noHtmlPatternValidation(emailSubjectLabel)
]);
function validateCustomerName(value, relationship) {
    const isValid = relationshipModel.customerName(relationship).trim().toLowerCase() === value.trim().toLowerCase();
    return isValid || "Company name doesn't match";
}
export function AgreementTerminationModal(props) {
    const { t: terminateAgreementTexts } = useLocaleKeys('hooks.terminateAgreement');
    const { t: commonTexts } = useLocaleKeys('common');
    const { relationshipId, dismissModal } = props;
    const { api } = useStoreActions();
    const businessId = useStore(selectors.user.businessId);
    const [notifyClient, setNotifyClient] = useState(false);
    const { control, setValue, handleSubmit } = useForm({
        mode: 'all',
        defaultValues: {
            companyName: '',
            emailSubject: '',
            emailContent: ''
        }
    });
    const { data, loading, renderRestfulState } = useRestfulWrapper({
        fetch: ()=>Promise.all([
                api.relationship.get(relationshipId),
                api.notification.getNotificationSettings(businessId)
            ])
    });
    const [, notificationSettings] = data !== null && data !== void 0 ? data : [];
    const standardVariant = notificationSettings === null || notificationSettings === void 0 ? void 0 : notificationSettings.customer.relationshipTerminated.variants[standardVariantKey];
    const allowedSystemKeys = useMemo(()=>{
        var _ref;
        return new Set(Object.keys((_ref = standardVariant === null || standardVariant === void 0 ? void 0 : standardVariant.templateVariables) !== null && _ref !== void 0 ? _ref : {}));
    }, [
        standardVariant === null || standardVariant === void 0 ? void 0 : standardVariant.templateVariables
    ]);
    useEffect(()=>{
        if (standardVariant) {
            setValue('emailSubject', standardVariant.subject);
            setValue('emailContent', standardVariant.message);
        }
    }, [
        standardVariant
    ]);
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'agreement_termination_confirm'
            },
            secondaryButton: {
                name: 'agreement_termination_cancel'
            }
        },
        title: terminateAgreementTexts.title(),
        skin: "danger",
        textAlignCenter: false,
        primaryButtonLabel: notifyClient ? commonTexts.buttons.terminateAndNotify() : commonTexts.buttons.terminate(),
        primaryButtonDisabled: loading,
        secondaryButtonLabel: commonTexts.buttons.cancel(),
        onPrimaryButtonClick: handleSubmit((param)=>{
            let { emailSubject, emailContent } = param;
            return dismissModal({
                confirmed: true,
                notification: notifyClient ? {
                    subject: emailSubject,
                    message: emailContent
                } : undefined
            });
        }),
        onSecondaryButtonClick: ()=>dismissModal({
                confirmed: false
            }),
        content: renderRestfulState({
            renderLoader: ()=>/*#__PURE__*/ _jsx("div", {
                    className: styles.loading,
                    children: /*#__PURE__*/ _jsx(Spinner, {
                        testId: "termination-modal-loader"
                    })
                }),
            renderChild: (param)=>{
                let { data: [relationship] } = param;
                return /*#__PURE__*/ _jsxs("div", {
                    className: styles.modalContentWrapper,
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsxs(Typography, {
                                    children: [
                                        "Terminating the agreement with",
                                        ' ',
                                        /*#__PURE__*/ _jsx(Typography, {
                                            className: styles.customerName,
                                            as: "span",
                                            weight: "bold",
                                            children: relationshipModel.customerName(relationship)
                                        }),
                                        ' ',
                                        "will immediately stop new invoices issuance and the initiation of payments for pending invoices."
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("br", {}),
                                /*#__PURE__*/ _jsx(Typography, {
                                    children: terminateAgreementTexts.notePaymentsInProcess()
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    children: terminateAgreementTexts.ensureFinalInvoicesSettled()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Controller, {
                            name: "companyName",
                            control: control,
                            rules: {
                                validate: (value)=>validateCustomerName(value, relationship)
                            },
                            render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                    bi: {
                                        name: 'termination_company_name_confirm'
                                    },
                                    collapseHelperText: false
                                }, connectControllerToTextField(p)), {
                                    label: terminateAgreementTexts.confirmLabel(),
                                    testId: "company-name-verification"
                                }))
                        }),
                        /*#__PURE__*/ _jsxs(Callout, {
                            children: [
                                terminateAgreementTexts.learnMoreCallout(),
                                ' ',
                                /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                    openInNewTab: true,
                                    href: helpArticles.howToEditAgreement(),
                                    bi: {
                                        name: 'how_to_edit_agreement_learn_more'
                                    },
                                    children: terminateAgreementTexts.learnMoreLink()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.notificationEmailFields,
                            children: [
                                /*#__PURE__*/ _jsx(Checkbox, {
                                    testId: "toggle-notify",
                                    onChange: ()=>setNotifyClient((prev)=>!prev),
                                    bi: {
                                        name: 'termination_notify_client'
                                    },
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        children: terminateAgreementTexts.notifyClient()
                                    })
                                }),
                                notifyClient && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Controller, {
                                            rules: emailSubjectRules,
                                            control: control,
                                            name: "emailSubject",
                                            render: (param)=>{
                                                let { field, fieldState } = param;
                                                return /*#__PURE__*/ _jsx(NotificationEditField, {
                                                    label: emailSubjectLabel,
                                                    value: field.value,
                                                    onChange: field.onChange,
                                                    error: fieldState.error != null,
                                                    bi: {
                                                        name: 'termination_email_subject'
                                                    },
                                                    testId: "email-subject",
                                                    allowedSystemKeys: allowedSystemKeys
                                                });
                                            }
                                        }),
                                        /*#__PURE__*/ _jsx(Controller, {
                                            rules: emailContentRules,
                                            control: control,
                                            name: "emailContent",
                                            render: (param)=>{
                                                let { field, fieldState } = param;
                                                return /*#__PURE__*/ _jsx(NotificationEditField, {
                                                    multiline: true,
                                                    minRows: 10,
                                                    label: emailContentLabel,
                                                    value: field.value,
                                                    onChange: field.onChange,
                                                    error: fieldState.error != null,
                                                    bi: {
                                                        name: 'termination_email_content'
                                                    },
                                                    testId: "email-content",
                                                    allowedSystemKeys: allowedSystemKeys,
                                                    adornmentClassName: styles.messageAdornment
                                                });
                                            }
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                });
            }
        })
    });
}
